// R5.2 / R5.3 — `RubricReadView`: THE REVIEW SURFACE (spec §1.1, §3, §4, §5).
//
// Route-level component. Owns the fetch, the freshness envelope, the flag decision, the §4 layer stack and — as
// of R5.3 — §3's fresh-CV journey and §5's correction loop. It still computes NO band and re-derives NO copy:
// every write it now issues is a correction the SERVER recomputes, and what comes back replaces the whole read
// rather than patching one in place.
//
// THE FLAG IS SERVER-AUTHORITATIVE, AND THIS CLIENT HOLDS NO FLAG CONSTANT (§8.3, spec rev 14). The decision is
// not read from a bundle constant and is never cached across sessions: it arrives on the SESSION/BOOTSTRAP
// payload (`GET /api/auth/me`'s `rubric_read`, §0a scope item 8), computed server-side from the authenticated
// account. That is §8.3's actual design, and the typed `403 { "error": "rubric_read_disabled" }` is what §8.3
// calls it — MISMATCH HANDLING, for a stale bundle meeting a server that has since decided otherwise — not the
// decision channel.
//
// WHY THAT DISTINCTION IS LOAD-BEARING. If the read request itself were the decision, then a backend that does
// not have these routes yet — a partial deploy, a rollback — answers with something unrecognized, and an
// unrecognized answer is indistinguishable from "not in the cohort". Reading it as the latter would drop a PAID
// CV onto the legacy surface, whose mount calls the legacy `/quality` endpoint, which RECOMPUTES when no
// parseable stored output exists (§8.4's own reason the route cannot become read-only by deletion). So an
// unrecognized answer FAILS CLOSED here: the registered unavailability string, never the legacy view.
//
// EITHER WAY THE FALLBACK COPY IS CHOSEN BY ASKING. On a genuine flag-off decision the surface probes
// `GET .../legacy` FIRST (it is flag-exempt, so it answers even for a flag-off account) and chooses
// `CS-RUBRIC-DISABLED` when a legacy read exists over `CS-RUBRIC-DISABLED-NO-LEGACY` when it would 404, "never
// claiming a fallback that isn't there" — and a probe that CANNOT ANSWER (transport failure, or the typed
// `409 legacy_read_corrupt`) is reported as its own error state rather than being read as absence (R5-OQ-J).
//
// §3's FRESH-CV JOURNEY LANDED IN R5.3 (R5-OQ-G), and it is entered from ONE state and one condition: the typed
// `no_ledger`, for a CV the parent says came straight from the import flow. Everything else that reaches
// `no_ledger` — a CV opened from the picker, a reload — still gets R5.2's `CS-EMPTY-NO-READ`, because extraction
// is paywalled, costs an LLM round trip, and binds the account's one free demo to this CV permanently (R5-OQ-C).
// Starting it because somebody opened a page would spend that on their behalf.
//
// §5's CORRECTION LOOP IS THE OTHER HALF, and its one hard rule is the §1.5 precondition: this surface presents
// only tokens the SERVER issued, never one it assembled or incremented, and holds no token at all across a change
// of CV. Every state in which it has none disables editing and says so (`CS-EDIT-DISABLED`). See
// `corrections.ts` for the four rules and why each is a rule rather than a habit.
//
// AND THE PREVIOUS-FORMAT CONTENT COMES FROM THE ENDPOINT BUILT FOR IT, NOT FROM THE LEGACY SURFACE (round-2
// finding 1). `QualityReadView` — the `legacyView` prop — cannot load for a COHORT account: its mount calls
// `/api/coverage/cv/:id/quality`, which answers a cohort account with the typed R5 pointer before any recompute
// (§0a item 6(b)) and which that surface reads as a failed load. So `legacyView` is consumed by exactly ONE state
// here, `disabled`, which by definition means the server said this account is NOT in the cohort. The cohort state
// that owes the applicant previous-format content — `legacy_only` — mounts `LegacyStoredView` against the
// dedicated, never-computing, flag-exempt `GET .../legacy` instead. State N shows the ratified empty string,
// because `GET /cv/:id` checks L before N and so `no_ledger` means there is no legacy read either.
//
// ORDER ON THE PAGE IS FIXED AND NEVER REORDERED BY BAND OR CONTENT (§4, T-ORDER-1). The layer stack below is
// §4's own subsection order, top to bottom, and the components inside each layer keep the payload's order.
import { useCallback, useEffect, useMemo, useReducer, useRef, useState, type ReactNode } from 'react';
import { api } from '../lib/api';
import type { RubricReadPublicV1 } from '../../../workers/src/lib/rubricReadPublic';
// THE STRUCTURAL VALIDATOR, SHARED WITH THE WORKER (round-2 finding 3). `rubricReadShape.ts` is pure — no D1, no
// Hono, no environment — exactly like `rubricCopy.ts`, which this surface already imports. Sharing the module is
// what makes "the client refuses what the server would refuse" a property rather than a synchronization chore.
import { isValidPublicShape } from '../../../workers/src/lib/rubricReadShape';
import type { EntryRecord } from '../../../workers/src/lib/factLedger';
import { LegacyStoredView } from './LegacyStoredView';
import { Copy } from './renderContent';
import { COPY, type CopyStringId } from './copy';
import { IntendedUse, IntendedUseHeader } from './IntendedUse';
// A.5-P.21 — the per-domain band sentences, moved off the opening line and behind one disclosure.
import { OverviewDetail } from './OverviewDetail';
import { SpecialtyOverlayPanel } from './SpecialtyOverlayPanel';
import type { ConventionReextractState } from './SpecialtyConventionPanel';
import type { LedgerEntryIndex } from './Recommendations';
// R5.7b (owner directive 2026-08-22) — THE READ RENDERS THROUGH THE LEGACY PRESENTATION, AS IS. `QualityReadBody`
// is `QualityReadView`'s own L1/L2 body with the fetch moved out; `toLegacyQualityRead` supplies its data model
// from the engine's read. R5.7b fix (Claude+Sol adjudicated under owner delegation 2026-08-22): NO per-entry block
// inside a legacy card — each entry card carries ONE text link (`EntryFactsLink`, `CS-ENTRY-FACTS-LINK`) to the
// entry's card on the facts screen (the rubric ledger panel, rendered BELOW the legacy body), where the facets,
// descriptors, per-entry confidence and known-fact / phrasing recommendations are reported. See `legacyAdapter.ts`
// for the mapping table.
import { QualityReadBody, type EntryFactsLink } from '../QualityReadView';
import { toLegacyQualityRead } from './legacyAdapter';
import { EntryFacets } from './FacetStrip';
import { EntryDescriptors } from './Descriptors';
import { EntryConfidenceLine } from './ConfidenceBadge';
import { EntryRecommendations, nonQuestionRecommendations } from './Recommendations';
// R5.3 — Layer 1, the §3 confirmation screen, the §5 correction loop, and the §5 step-5 diff.
import { EntryFactsDisclosure, EntryStateNotices, FactLedgerPanel, ledgerEntryDomId, type LedgerEntryLike } from './FactLedgerPanel';
import { BandChangeDiff } from './BandChangeDiff';
import {
  canSubmit, correctionReducer, initialCorrectionState, mintCorrectionKey, submitCorrection,
  type Correction, type CorrectionState,
} from './corrections';
import {
  buildEntryHeadings,
  buildEvidenceIndex,
  entryHeadingOf,
  EMPTY_EVIDENCE_INDEX,
  type EntryHeading,
  type EvidenceIndex,
  type LedgerPayload,
} from './evidenceIndex';
import { BODY_STYLE, MUTED_STYLE, NOTICE_STYLE, PAGE_STYLE, SECTION_STYLE } from './styles';

/* ============================================================================================================== *
 * THE STATE MACHINE — one state per typed server answer, never a catch-all
 * ============================================================================================================== */

export type RubricViewState =
  | { kind: 'loading' }
  /** A stored read, served ungated. `freshness` decides whether the stale banner rides above it. */
  | { kind: 'read'; read: RubricReadPublicV1 }
  /**
   * The bootstrap said `rubric_read: false`, or the read answered the typed `403 rubric_read_disabled` (§8.3's
   * mismatch case). `hasLegacy` was chosen by ASKING `/legacy`, never assumed.
   */
  | { kind: 'disabled'; hasLegacy: boolean }
  /** `409 legacy_only` — freshness state L: a legacy read exists and no current row does. */
  | { kind: 'legacy_only' }
  /**
   * `409 no_ledger` — state N with nothing to compute from, and NOTHING PREVIOUS-FORMAT EITHER: `GET /cv/:id`
   * checks freshness state L before N, so a CV with a stored legacy read answers `legacy_only` and never reaches
   * here. This state therefore means exactly "no review of any kind exists for this CV yet", which is what
   * `CS-EMPTY-NO-READ` says. Reached from the server's own typed answer, never by a fallback that could not tell
   * this case apart from a broken deploy. The creation journey (import → extraction → confirmation → read) is
   * R5.3's exit and lives outside this component; until then this state offers the way back to it.
   */
  | { kind: 'no_stored_read' }
  /**
   * R5.3, §3's FRESH-CV JOURNEY, step 1 — extraction is running for a CV that just arrived from the import flow.
   *
   * Entered ONLY from `no_ledger` and ONLY when the parent said this CV came straight from an import
   * (`startExtraction`). A CV that merely happens to have no ledger — one the applicant opened from their list —
   * still gets `no_stored_read`, because starting a paywalled LLM call because somebody opened a page would spend
   * their one free demo without their asking.
   */
  | { kind: 'extracting' }
  /**
   * §3's FRESH-CV JOURNEY, step 2 — the CONFIRMATION SCREEN, "skippable but never hidden".
   *
   * Reached with a ledger and no read. Every correction posted from here is §5 step 0 (ledger-only): the first
   * assessment for this CV is produced exclusively by the gated GET the proceed control runs, which is what keeps
   * a state-N CV from PATCHing its way to a free first read.
   */
  | { kind: 'confirming' }
  /**
   * The journey could not start or could not finish, and Appendix A has no string for the specific reason.
   *
   * ROUND 2: THEY NO LONGER COLLAPSE (build-review ruling 4). Round 1 rendered `CS-ERR-READ-UNAVAILABLE` for all
   * six, and the review rejected it: that string ends "Try again in a moment", which is accurate for a transport
   * failure and FALSE for a daily budget halt — retrying in a moment fails again, all day. Each category now has
   * its own registered A.5-P sentence, and each says what is actually true and what actually helps:
   *
   *   `409 extraction_in_progress`                              → `CS-EXTRACT-IN-PROGRESS` (wait for the other one)
   *   `409 extraction_conflict` / `…_attempt_superseded`        → `CS-EXTRACT-SUPERSEDED` (re-read; do not retry —
   *                                                               a second attempt is refused by the commit fence)
   *   `503 free_llm_at_capacity`                                → `CS-EXTRACT-FREE-CAPACITY` (resets tomorrow)
   *   `429 llm_account_capped`                                  → `CS-EXTRACT-ACCOUNT-LIMIT` (resets tomorrow)
   *   `502 extract_call_failed`                                 → `CS-EXTRACT-CAPACITY` (you can try again)
   *   anything else                                             → `CS-ERR-READ-UNAVAILABLE`
   *
   * The budget refusals' own `userError` prose is still NOT rendered: it predates Appendix A, and putting
   * unregistered system copy on this surface is what T-COPY-1 forbids. What changed is that the registered
   * alternative is now accurate rather than merely safe.
   */
  | { kind: 'extract_unavailable'; copyId: CopyStringId }
  /** `409 stored_read_corrupt` — freshness state X. Recoverable by the free `/refresh`. */
  | { kind: 'corrupt' }
  /** `/refresh` answered `409 corrupt_unrecoverable_no_ledger`: no retry affordance, because none exists. */
  | { kind: 'unrecoverable' }
  /** `404 cv_not_found` — there is no such CV to review. */
  | { kind: 'empty' }
  /** `402 paywall_required` — handed to the existing paywall wiring, byte-identical to the coverage read's. */
  | { kind: 'paywall' }
  /**
   * NOBODY REPORTED A DECISION THIS CONTRACT RECOGNIZES — the bootstrap carried no usable `rubric_read`, or the
   * read answered a status/body shape that is not in the contract.
   *
   * FAIL CLOSED, AND SPECIFICALLY *NOT* TO THE LEGACY VIEW. §8.3's own principle is "unset, empty, or
   * unparseable ⇒ OFF", and R5.1 applied exactly that server-side. But "off" here cannot mean "mount the legacy
   * surface": in practice this state IS the half-deployed backend, and on such a backend the R5 cohort guard may
   * not exist at all while `QualityReadView`'s mount calls `/quality`, which recomputes when no parseable stored
   * output exists. A paid CV could enter legacy compute through precisely the answer that proves we do not know
   * what is deployed. The honest, safe answer is the registered unavailability string and nothing else.
   */
  | { kind: 'unavailable' }
  /** Anything else, including a transport failure. */
  | { kind: 'error' };

/** A refresh that could not run says so in place, without destroying the read the user is looking at. */
type RefreshNote = 'none' | 'disabled';

interface ErrorBody {
  error?: string;
  reason?: string;
}

/**
 * The served read envelope: the payload, plus the composite `If-Match` its correction loop will present (§1.5).
 *
 * `precondition` is a SIBLING of `read`, never a field inside it — `RubricReadPublicV1`'s shape is frozen at
 * `scoring_version: 5` (§11 item 1) and `isValidPublicShape` refuses anything the §1.2a projection does not name,
 * so transport metadata about revision position lives beside the payload exactly as an `ETag` header would.
 */
interface ReadEnvelope {
  read?: unknown;
  precondition?: unknown;
  /**
   * R7.2 — the §6b SIGNALS RE-EXTRACTION OFFER, a sibling of `read` for the same reason `precondition` is: it is
   * transport metadata about the ledger's position relative to the active signal set, not part of the frozen
   * `RubricReadPublicV1` shape. Present only when the CV's ledger holds no `specialty_signal` facts of the set
   * the CV's specialty asks for — which is exactly when every §6b row renders `CS-CONV-UNBOUND`.
   */
  signals_reextraction?: unknown;
}

/** The offer, narrowed to the fields this surface actually spends. */
interface ReextractOffer {
  reason: 'never_extracted' | 'set_changed';
  expected_set_version: string;
  endpoint: string;
  then_refresh: string;
}

/**
 * NARROWED STRICTLY, LIKE `precondition` — and for a heavier reason: acting on this offer SPENDS AN LLM CALL.
 *
 * Every field is checked, and the two reasons are an allowlist rather than a default: a future reason this
 * surface has never seen must not auto-run a paid pass on the strength of a string nobody here recognizes. The
 * endpoints are required to be same-origin absolute paths — an offer is server-authored, but a POST to whatever a
 * body says is a redirect primitive, and this one is issued automatically with the applicant's credentials.
 */
function offerOf(body: ReadEnvelope | null | undefined): ReextractOffer | null {
  const o = body?.signals_reextraction;
  if (!o || typeof o !== 'object') return null;
  const r = o as Record<string, unknown>;
  if (r.reason !== 'never_extracted' && r.reason !== 'set_changed') return null;
  if (typeof r.expected_set_version !== 'string' || r.expected_set_version.length === 0) return null;
  if (typeof r.endpoint !== 'string' || !r.endpoint.startsWith('/api/')) return null;
  if (typeof r.then_refresh !== 'string' || !r.then_refresh.startsWith('/api/')) return null;
  return {
    reason: r.reason,
    expected_set_version: r.expected_set_version,
    endpoint: r.endpoint,
    then_refresh: r.then_refresh,
  };
}

/**
 * The token, or `null` — and `null` is a real answer, not a missing one.
 *
 * Typed `unknown` and narrowed strictly, for the same reason `rubric_read` is: a backend that predates this field
 * must produce "we hold no token" (editing disabled, `CS-EDIT-DISABLED`), never a coerced value that would be
 * presented as a precondition asserting a state nobody reported.
 */
function preconditionOf(body: ReadEnvelope | null | undefined): string | null {
  return typeof body?.precondition === 'string' && body.precondition.length > 0 ? body.precondition : null;
}

/**
 * Fold ONE corrected entry's facts back into the evidence index.
 *
 * WHY THIS IS NEEDED AT ALL. The index is built once per ledger fetch, and a correction changes the ledger without
 * refetching it — so an edited fact would keep resolving "show source" to the sentence the applicant just
 * replaced. Only the corrected entry's facts are re-folded; every other entry's entries are untouched, because
 * nothing about them moved.
 */
function mergeEntryEvidence(prev: EvidenceIndex, echo: unknown): EvidenceIndex {
  // Reuse the ONE index builder rather than re-deriving the fact→text rule here (a second implementation of it
  // would be a second opinion about which provenances carry applicant text).
  const fresh = buildEvidenceIndex({ entries: [echo as { entry_id?: unknown; facts?: unknown }] });
  if (fresh.size === 0) return prev;
  const next = new Map(prev);
  for (const [id, resolved] of fresh) next.set(id, resolved);
  return next;
}

/** The same fold for the entry's heading, which an edit to `role_title` (or `institution`) legitimately changes. */
function mergeEntryHeading(
  prev: ReadonlyMap<string, EntryHeading>, echo: unknown,
): ReadonlyMap<string, EntryHeading> {
  const id = (echo as { entry_id?: unknown })?.entry_id;
  if (typeof id !== 'string') return prev;
  const next = new Map(prev);
  const heading = entryHeadingOf(echo);
  // An echo that carries nothing to name the entry by REMOVES the heading rather than leaving the old one
  // standing: every call site renders no pointer at all rather than a stale one.
  if (heading === null) next.delete(id);
  else next.set(id, heading);
  return next;
}

/**
 * The session/bootstrap payload, as far as this surface reads it (§8.3, §0a item 8).
 *
 * `rubric_read` is DELIBERATELY TYPED `unknown`: the whole point of consuming a bootstrap field is that an older
 * backend may not send it, and a field typed `boolean | undefined` invites `?? false` — which would silently
 * convert "this backend never answered" into "this account is not in the cohort", the exact conflation the 403-
 * as-decision design suffered from. It is narrowed by `decideFromBootstrap` below, which has a third answer.
 */
interface BootstrapPayload {
  rubric_read?: unknown;
}

/** What the bootstrap told us. `unknown` is a real answer here, not a missing one. */
type FlagDecision = 'on' | 'off' | 'unknown';

/**
 * Narrow the bootstrap's `rubric_read` to a decision. STRICTLY BOOLEAN, both ways.
 *
 * `true`/`false` are decisions the server actually reported. ANYTHING ELSE — absent, null, a string, a number
 * from some future shape — is `'unknown'`, and `'unknown'` fails closed to the unavailability state rather than
 * to the legacy view. §8.3's flag parser makes the same choice server-side ("anything not exactly one of the
 * three literals is off — including `"true"`, which is not a mode"); this is that rule on the client's side of
 * the wire, with the one difference that the client can also be talking to a backend that predates the field.
 */
export function decideFromBootstrap(body: BootstrapPayload | null | undefined): FlagDecision {
  if (body?.rubric_read === true) return 'on';
  if (body?.rubric_read === false) return 'off';
  return 'unknown';
}

/** What the flag-exempt `/legacy` probe found. `error` is DISTINCT from `absent` (R5-OQ-J). */
type LegacyProbe = 'present' | 'absent' | 'error';

/**
 * Probe `GET .../legacy` — flag-exempt, read-only, and the ONLY honest way to choose between the two disabled
 * strings (§8.3: "never claiming a fallback that isn't there").
 *
 * THREE ANSWERS, NOT TWO (ruling R5-OQ-J, spec rev 14). The previous shape treated a transport failure and the
 * typed `409 legacy_read_corrupt` as "no legacy on file", which is a FALSE ABSENCE STATEMENT: the surface told an
 * applicant that no previous-format review exists for this CV when one may exist and merely failed to load, or
 * exists and will not parse. `CS-RUBRIC-DISABLED-NO-LEGACY` asserts a fact about their stored data, and it may
 * only render when the server said so — the typed `404 no_legacy_read`. Everything else is an error about our
 * ability to answer, and is reported as one.
 */
async function probeLegacy(cvId: string): Promise<LegacyProbe> {
  let res: { ok: boolean; status: number; data: ErrorBody | null };
  try {
    res = await api<ErrorBody>(`/api/rubric-read/cv/${cvId}/legacy`);
  } catch {
    return 'error'; // transport — we could not ask, so we cannot say
  }
  if (res.ok) return 'present';
  // The ONE typed answer that means "none exists". `409 legacy_read_corrupt` is the opposite: a legacy read is on
  // file and cannot be served, which is not an absence and must not be reported as one.
  if (res.status === 404 && res.data?.error === 'no_legacy_read') return 'absent';
  return 'error';
}

/**
 * ONE TYPED REFUSAL → ONE REGISTERED SENTENCE (build-review ruling 4).
 *
 * A closed table rather than a chain of conditionals in the handler, so "which categories exist" is a thing that
 * can be read in one place and asserted over in a test. Anything the table does not name falls back to the
 * general unavailability string — which is the honest answer for an answer we do not recognize, and the WRONG
 * answer for the four we do, which is why they are named.
 */
const EXTRACT_REFUSAL_COPY: Readonly<Record<string, CopyStringId>> = Object.freeze({
  extraction_in_progress: 'CS-EXTRACT-IN-PROGRESS',
  extraction_conflict: 'CS-EXTRACT-SUPERSEDED',
  extraction_attempt_superseded: 'CS-EXTRACT-SUPERSEDED',
  free_llm_at_capacity: 'CS-EXTRACT-FREE-CAPACITY',
  llm_account_capped: 'CS-EXTRACT-ACCOUNT-LIMIT',
  extract_call_failed: 'CS-EXTRACT-CAPACITY',
});

export function extractRefusalCopyId(status: number, error: string | undefined): CopyStringId {
  const byError = error === undefined ? undefined : EXTRACT_REFUSAL_COPY[error];
  if (byError !== undefined) return byError;
  // A 502 with no recognized error code is still a provider failure, and "you can try again" is true of it.
  if (status === 502) return 'CS-EXTRACT-CAPACITY';
  return 'CS-ERR-READ-UNAVAILABLE';
}

export interface RubricReadViewProps {
  cvId: string;
  /** The labelled formative beta (§8.3's rollout). Pins `CS-BETA-LABEL` beside the short intended-use line. */
  beta?: boolean;
  /** The existing paywall wiring, reused unchanged — the 402 shape is byte-identical to the coverage read's. */
  onPaywall?: (cvId: string) => void;
  /**
   * The legacy view, rendered BENEATH the FLAG-OFF notice — and, since round 2, NOWHERE ELSE.
   *
   * THE ENTRY DECISION IS STRUCTURAL, NOT A SECOND FLAG CHECK IN THE PARENT (round-2 finding 1). This prop is
   * consumed by exactly one state: `disabled`, which means the SERVER decided this account is not in the R5
   * cohort. That is the only state in which the legacy surface works, because `GET /api/coverage/cv/:id/quality`
   * answers a COHORT account with a typed R5 pointer before any recompute (§0a item 6(b)) and the legacy surface
   * does not understand that shape. The cohort states that need previous-format content mount `LegacyStoredView`
   * against the dedicated, never-computing `/legacy` endpoint instead. So "a cohort account never mounts the
   * legacy surface" is a property of this component's own branch table, checkable here, rather than a duplicate
   * of the server's decision maintained in `App.tsx` — where a second copy could drift from the first.
   *
   * Still passed as an element rather than imported, so `rubric/` never depends on the module it replaces:
   * `QualityReadView` retires at §8.4 and nothing here has to change when it does.
   */
  legacyView?: ReactNode;
  /**
   * Navigation back to the evaluate flow, offered on the no-stored-read empty state (R5-OQ-G's interim routing).
   * Optional: without it the state still renders its registered sentence, just without a control.
   */
  onImportCv?: () => void;
  /**
   * R5.3 — "this CV just came from the import flow", which is what turns §3's fresh-CV journey on.
   *
   * THE COHORT DECISION IS STILL NOT THE PARENT'S (round-2 finding 1). This flag says nothing about whether the
   * account is in the R5 cohort; it says only where the user arrived from, which is a fact `App.tsx` knows and
   * this component cannot. The journey it enables is reachable only from the `no_ledger` state, which is reached
   * only after the SERVER's bootstrap decision said this account IS in the cohort — so "a non-cohort account never
   * enters the R5 journey" remains a property of this component's own branch table, exactly as the flag-off
   * legacy mount does.
   */
  startExtraction?: boolean;
  /** Bumped by the parent to force a re-read (e.g. after the paywall clears). */
  reloadKey?: number;
  /**
   * R5.7b — the three things the legacy presentation takes from its parent: the specialty's DISPLAY name (for the
   * legacy panel heading; never a specialty id), and the two navigation callbacks the legacy page carries ("Back to
   * home", "Keep building this CV"). All optional; the legacy body renders its controls either way.
   */
  specialtyName?: string;
  onHome?: () => void;
  onCraft?: () => void;
}

export function RubricReadView({
  cvId, beta = false, onPaywall, legacyView, onImportCv, startExtraction = false, reloadKey = 0,
  specialtyName = '', onHome, onCraft,
}: RubricReadViewProps) {
  const [state, setState] = useState<RubricViewState>({ kind: 'loading' });
  // R5.7b — the legacy presentation's L1 ⇄ L2 position (the section under review, or null for the overview). Held
  // HERE, not in the body: a correction replaces the read while the applicant is inside a section's L2 (the facts
  // disclosure lives there), and the position must survive that replacement. It resets with the source.
  const [focusSection, setFocusSection] = useState<string | null>(null);
  const [refreshNote, setRefreshNote] = useState<RefreshNote>('none');
  const [evidence, setEvidence] = useState<EvidenceIndex>(EMPTY_EVIDENCE_INDEX);
  // R5.7 — the entry HEADINGS (role title → institution → raw text, plus dates) are the state; R5.7b's legacy
  // adapter derives the plain id → text labels it needs from the ledger itself.
  const [entryHeadings, setEntryHeadings] = useState<ReadonlyMap<string, EntryHeading>>(new Map());
  const [ledgerEntries, setLedgerEntries] = useState<LedgerEntryIndex>(new Map());
  const [busy, setBusy] = useState(false);

  /* ---- R7.2 — THE §6b SIGNALS RE-EXTRACTION (the offer the read has always carried, now taken) -------------- */

  const [offer, setOffer] = useState<ReextractOffer | null>(null);
  const [reextractBusy, setReextractBusy] = useState(false);
  const [reextractFailed, setReextractFailed] = useState(false);
  /**
   * ONCE PER CV AND SET, and once per FAILURE, both keyed `${cvId}:${set_version}`.
   *
   * `ranRef` is what makes the auto-run idempotent: the pass costs an LLM call, and the read that comes back from
   * the refresh can legitimately still carry an offer (the pass found no signals in this CV at all — a real
   * answer, not a failure), which without this memory would be an unbounded spend loop. `failedRef` is the same
   * guarantee for the failure path: one automatic attempt per session, after which the applicant decides by
   * pressing the CTA. Refs, not state, because a re-render must not re-open a decision already made.
   */
  const ranRef = useRef<Set<string>>(new Set());
  const failedRef = useRef<Set<string>>(new Set());

  /* ---- R5.3 — THE CORRECTION LOOP'S STATE ------------------------------------------------------------------ */

  /**
   * The §1.5 precondition state, in its own reducer (`corrections.ts`).
   *
   * It is seeded per `cvId` and RESET on every source change below, for the same reason the applicant-text maps
   * are: a precondition names a ledger, ledger ids are not interchangeable across CVs, and a token carried into
   * the next CV would be presented against a ledger it does not name.
   */
  const [correction, dispatch] = useReducer(correctionReducer, cvId, initialCorrectionState);
  /**
   * THE CURRENT TOKEN, mirrored into a ref during render (R7.2).
   *
   * The re-extraction presents the same `If-Match` every other writer does, but it RE-READS it after its own
   * conflict refetch — and the refetch's `dispatch` is not visible to a callback closed over `correction`. The ref
   * is what makes "retry with the fresh token" mean the token the refetch actually installed, rather than the one
   * the attempt already failed with. Assigned in render for the same reason `sourceRef` is.
   */
  const correctionRef = useRef<string | null>(correction.precondition);
  correctionRef.current = correction.precondition;

  /**
   * The read the applicant was looking at BEFORE the correction that is now on screen.
   *
   * This is what `band_changes` is diffed against: the payload carries raw band enums and no copy ids, and the
   * previous read is the only place the id the engine stamped on the OLD band still exists (`BandChangeDiff`).
   */
  const [previousRead, setPreviousRead] = useState<RubricReadPublicV1 | null>(null);

  /**
   * §5's OTHER half of the flag escalation (round 2, finding 6): the domain ids the last correction's exclusion
   * reached, as the server derived them.
   *
   * SESSION-SCOPED ON PURPOSE, and this is a stated limit rather than an oversight. The escalation is a statement
   * about a TRANSITION — "this domain lost a unit because of what you just did" — and after the recompute the
   * excluded unit is gone from every list on the stored read, so a later GET has nothing to derive it from and
   * §1.2a forbids recomputing at serve time to find out. The ledger card's own `CS-FLAG-ESCALATED` is persistent
   * (it follows `confirmation_state`); the per-domain notice belongs to the correction that caused it.
   */
  const [affectedDomains, setAffectedDomains] = useState<readonly string[]>([]);

  /**
   * The read currently ON SCREEN, mirrored into a ref.
   *
   * Read by the correction handler when it needs to know what it is about to replace. A ref rather than a closure
   * over `state`, because `correct` is memoized on the precondition and would otherwise capture whichever read
   * was current when it was last created — which is not necessarily the one the applicant is looking at after a
   * refetch.
   */
  const onScreen = useRef<RubricReadPublicV1 | null>(null);

  /** Bumped after an extraction, to re-run the ledger fetch for a ledger that did not exist a moment ago. */
  const [ledgerKey, setLedgerKey] = useState(0);

  /**
   * §3's journey runs AT MOST ONCE per (cvId, reloadKey).
   *
   * Extraction is the one genuinely expensive, genuinely gated step in the whole surface, and it binds the
   * account's one free demo to this CV permanently (R5-OQ-C). A retry loop over it — the shape a bare `useEffect`
   * on a state value invites — would burn LLM budget and could bind a claim the applicant never asked to spend.
   */
  const journeyRan = useRef('');

  /**
   * THE GENERATION GUARD for the decision/read/probe chain (round-2 advisory).
   *
   * The ledger effect always had a `live` flag; this chain did not, and it is the one that decides WHICH SURFACE
   * MOUNTS. `load` awaits three requests in sequence, so a re-run for a new `cvId` or a bumped `reloadKey` can
   * easily overtake the previous run — and the previous run's last `setState` then wins. The worst case is not
   * cosmetic: an OLDER recognized `no_ledger`/flag-off result landing after a NEWER unrecognized bootstrap
   * replaces the fail-closed unavailability state with a legacy mount, which is precisely the substitution §8.3
   * fails closed to prevent. A monotonic counter, captured per run and rechecked before every write, makes a
   * superseded run inert; the cleanup bumps it again so an in-flight run cannot write into an unmounted view.
   */
  const gen = useRef(0);

  /**
   * THE SOURCE KEY — `${cvId}:${reloadKey}` — mirrored into a ref, updated DURING RENDER (round 2, finding 3).
   *
   * `gen` above guards the decision/read/probe chain, which is a single `load()` run. It cannot guard the
   * CORRECTION loop, because a correction is not part of that chain: `correct()` and `refetchRead()` are started
   * by the applicant and settle whenever the network settles, which may be after the parent has re-rendered this
   * component for a different CV. The build review found exactly that: an in-flight PATCH or conflict refetch
   * from CV A completing after a switch to CV B, restoring A's token and calling `setState` with A's read.
   *
   * WHY A REF UPDATED IN RENDER, rather than an effect. An effect runs AFTER the commit, so between the render
   * that changed `cvId` and the effect that recorded it there is a window in which a completion would still see
   * the old key and consider itself current — which is the whole window the guard exists to close. Assigning
   * during render makes the ref change at the same instant the props do, and the assignment is idempotent (same
   * value on every re-render for the same source), which is what makes it safe in StrictMode's double render.
   */
  const sourceKey = `${cvId}:${reloadKey}`;
  const sourceRef = useRef(sourceKey);
  sourceRef.current = sourceKey;
  /**
   * THE CV ALONE, mirrored the same way (E-5f live defect). `upgrade` used to guard on the FULL source key, and
   * an upgrade on a no-ledger CV is a 30–90 s extraction — long enough for `reloadKey` to be bumped for the SAME
   * CV (the paywall/checkout restore path does exactly that). The response then failed the guard, was dropped
   * silently, and `busy` was never settled: the server had written the read and the screen never showed it. The
   * thing the guard exists to prevent is a read of CV A landing under CV B's chrome — a property of the CV, not
   * of the reload generation — so the long-running action routes check THIS ref and let a same-CV key bump through.
   */
  const cvRef = useRef(cvId);
  cvRef.current = cvId;

  /**
   * The flag-off route, shared by the bootstrap's own `false` and by the read's typed 403 (§8.3's mismatch case).
   * Both mean the same thing to the user, so both choose the same copy the same way — by asking `/legacy`.
   *
   * `alive` is the caller's generation check, threaded through rather than re-derived: this function awaits a
   * request of its own, so it needs the same guard the run that called it is under.
   */
  const routeDisabled = useCallback(async (alive: () => boolean) => {
    const probe = await probeLegacy(cvId);
    if (!alive()) return;
    // A probe that could not answer does NOT get to pick between two strings, one of which asserts that no
    // previous-format review is on file for this CV (R5-OQ-J).
    if (probe === 'error') return setState({ kind: 'error' });
    return setState({ kind: 'disabled', hasLegacy: probe === 'present' });
  }, [cvId]);

  /**
   * §3's FRESH-CV JOURNEY, STEP 1 — `POST /api/fact-ledger/:cv_id/extract` (§0a).
   *
   * THIS IS THE GATED, EXPENSIVE STEP, and every one of its refusals is a distinct, typed fact:
   *   * `402 paywall_required` — the product gate (§0a item 1) or a demo already bound to a DIFFERENT CV
   *     (R5-OQ-C). Handed to the existing paywall wiring, byte-identically to the read's own 402.
   *   * `409 extraction_in_progress` — this account already has a live attempt (§0a's lease). Never treated as an
   *     idempotent replay of it, and never retried from here: a second attempt is refused by design.
   *   * `503 free_llm_at_capacity` / `429 llm_account_capped` — the BUDGET halts, which are about our spend
   *     ceilings and not about this applicant's entitlement.
   *   * `409 extraction_conflict` / `409 extraction_attempt_superseded` / `502 extract_call_failed` — commit-guard
   *     and provider failures.
   * All but the 402 collapse to `extract_unavailable`, whose copy problem is documented on that state.
   */
  const runExtraction = useCallback(async (alive: () => boolean) => {
    setState({ kind: 'extracting' });
    let res: Awaited<ReturnType<typeof api<{ precondition?: unknown } & ErrorBody>>>;
    try {
      res = await api<{ precondition?: unknown } & ErrorBody>(`/api/fact-ledger/${cvId}/extract`, {
        method: 'POST',
      });
    } catch {
      if (alive()) setState({ kind: 'extract_unavailable', copyId: 'CS-ERR-READ-UNAVAILABLE' });
      return;
    }
    if (!alive()) return;
    const body = (res.data ?? {}) as ErrorBody;
    if (res.ok) {
      // THE CONFIRMATION SCREEN'S PRECONDITION, issued by the route that just created the ledger. There is no read
      // to learn one from yet — that is the whole reason §3 puts confirmation BEFORE the gated GET — so the
      // extraction response is the only place it can come from.
      dispatch({ type: 'issued', cvId, precondition: preconditionOf(res.data) });
      // The ledger did not exist when the ledger effect last ran. Bumping its key is what makes the confirmation
      // screen have anything to confirm.
      setLedgerKey((k) => k + 1);
      return setState({ kind: 'confirming' });
    }
    if (res.status === 402 && body.reason === 'needs_paid_cv') {
      onPaywall?.(cvId);
      return setState({ kind: 'paywall' });
    }
    // `400 no_entries` — the CV parsed to nothing there is anything to review. That is not an error about our
    // availability; it is the empty state, and its registered sentence already names the remedy (re-import).
    if (res.status === 400 && body.error === 'no_entries') return setState({ kind: 'no_stored_read' });
    if (res.status === 404) return setState({ kind: 'empty' });
    setState({ kind: 'extract_unavailable', copyId: extractRefusalCopyId(res.status, body.error) });
  }, [cvId, onPaywall]);

  /**
   * The read itself. Passive: a stored current read is served without gating and without computing.
   *
   * `proceedToken` is §3's PROCEED CREDENTIAL (round 3, finding 1) — the exact composite precondition the
   * `needs_confirmation` 409 issued for this CV, presented back in `If-Match`. It is a PARAMETER rather than
   * component state because it is a property of ONE request, not of the view: the confirmation screen's proceed
   * control passes the token it was armed with, and every other caller (mount, reload, conflict recovery) passes
   * nothing at all. Round 2 sent a bare `?after_confirmation=1` here, which was bound to nothing and therefore
   * replayable by anyone who had ever seen the URL; the server now refuses it, and this is the only issuer of
   * what it accepts instead.
   */
  const load = useCallback(async (proceedToken: string | null = null) => {
    const mine = ++gen.current;
    const alive = () => gen.current === mine;
    setState({ kind: 'loading' });
    setRefreshNote('none');

    // ---- STEP 1: THE SERVER'S OWN COHORT DECISION, off the session/bootstrap payload (§8.3, §0a item 8).
    // Asked FIRST, and never inferred from how the read happens to answer.
    let bootstrap: Awaited<ReturnType<typeof api<BootstrapPayload>>>;
    try {
      bootstrap = await api<BootstrapPayload>('/api/auth/me');
    } catch {
      // A TRANSPORT FAILURE, not a decision about this account. Never falls through to the legacy surface.
      if (alive()) setState({ kind: 'error' });
      return;
    }
    if (!alive()) return;
    const decision = bootstrap.ok ? decideFromBootstrap(bootstrap.data) : 'unknown';
    if (decision === 'off') return await routeDisabled(alive);
    if (decision === 'unknown') {
      // FAIL CLOSED — and specifically not onto the legacy surface, whose mount can trigger legacy compute. See
      // the `unavailable` state's own banner: this is the partially-deployed-backend case, and the one thing we
      // must not do on a backend we cannot identify is send a paid CV down the recomputing path.
      return setState({ kind: 'unavailable' });
    }

    // ---- STEP 2: the read. The account IS in the cohort, so every answer below is about this CV, not about
    // whether this surface should exist for this account.
    let res: Awaited<ReturnType<typeof api<ReadEnvelope & ErrorBody>>>;
    try {
      res = await api<ReadEnvelope & ErrorBody>(
        `/api/rubric-read/cv/${cvId}`,
        // THE CREDENTIAL RIDES IN A HEADER, never in the URL. That is not decoration: a URL is bookmarkable,
        // shareable, and reproducible from a browser history entry, and the round-2 defect was exactly that the
        // permission to skip confirmation lived somewhere all three of those things could carry it.
        proceedToken ? { headers: { 'If-Match': proceedToken } } : {},
      );
    } catch {
      if (alive()) setState({ kind: 'error' });
      return;
    }
    if (!alive()) return;
    if (res.ok && res.data?.read) {
      // ---- THE BODY IS VALIDATED AT RUNTIME BEFORE ANYTHING READS IT (round-2 finding 3).
      //
      // A truthy `read` is not a contract. `res.data.read` used to be CAST to `RubricReadPublicV1` and handed
      // straight to `ReadBody`, which indexes `read.presentation`, `read.domains`, `read.freshness` — so a
      // partially deployed backend answering `200 {read: {…old shape…}}` produced a crash or a half-rendered
      // review of a payload no compute in this deploy could have made. Rev 14's rule is that an unrecognized
      // status OR BODY SHAPE fails closed, and this is the body half of it: the SAME structural validator the
      // worker refuses a stored row with, run here on the served envelope, before a single field is read.
      if (!isValidPublicShape(res.data.read)) return setState({ kind: 'unavailable' });
      // R5.3 — THE PRECONDITION ARRIVES WITH THE READ, and editing is enabled only because it did. A `200` whose
      // body carries no usable `precondition` (an older backend, a ledger with no revision row) still renders the
      // review; it just renders it read-only, with `CS-EDIT-DISABLED` saying so.
      dispatch({ type: 'issued', cvId, precondition: preconditionOf(res.data) });
      // R7.2 — the offer rides in with the read it describes, and is REPLACED (with `null` when absent) rather
      // than merged: an offer describes one ledger position, and this response is the newest word on it.
      setOffer(offerOf(res.data));
      // A fresh LOAD is not a correction, so there is no diff to draw against whatever was on screen before it —
      // and no escalation to report either, for the same reason.
      setPreviousRead(null);
      setAffectedDomains([]);
      setState({ kind: 'read', read: res.data.read });
      return;
    }
    const body = (res.data ?? {}) as ErrorBody;
    if (res.status === 403 && body.error === 'rubric_read_disabled') {
      // §8.3's MISMATCH case: this bundle's bootstrap said on, the server says off. The server is truth.
      return await routeDisabled(alive);
    }
    if (res.status === 402 && body.reason === 'needs_paid_cv') {
      onPaywall?.(cvId);
      setState({ kind: 'paywall' });
      return;
    }
    if (res.status === 409 && body.error === 'legacy_only') return setState({ kind: 'legacy_only' });
    if (res.status === 409 && body.error === 'stored_read_corrupt') return setState({ kind: 'corrupt' });
    // STATE N WITH NO LEDGER — and, since R5.3, the entry point of §3's fresh-CV journey.
    //
    // TWO DIFFERENT SITUATIONS ANSWER WITH THE SAME TYPED `no_ledger`, and they are not the same fact about the
    // applicant's intent: a CV that has just been imported (the journey; the parent said so) has no ledger BECAUSE
    // extraction has not run yet and running it is the next step they asked for — while a CV opened from the list
    // has no ledger and no pending request, and starting a paywalled LLM call there would spend their one free
    // demo, permanently bound to this CV (R5-OQ-C), because they clicked a link. The first extracts; the second
    // keeps R5.2's empty state.
    // §3's CONFIRMATION POINT, reported by the SERVER (spec rev 14 §3/§1.2 amendment). A ledger exists and no
    // read does, and this request carried no proceed marker — so the screen renders, exactly as it does on the
    // first pass through the journey. This is what makes "skippable but never hidden" survive a reload: round 1
    // recomputed here, so refreshing mid-confirmation produced the review the screen exists to precede.
    //
    // THE LEDGER FETCH IS BUMPED because this state is reachable on a COLD MOUNT — a reload, not only the
    // post-extraction transition — and on a cold mount the ledger effect has already run, so the panel would
    // otherwise render the screen with nothing on it.
    if (res.status === 409 && body.error === 'needs_confirmation') {
      // THE SCREEN IS ARMED BY THIS RESPONSE. On the first pass the token came from the extraction that created
      // the ledger; on a RELOAD there is no extraction, so without the token the screen would render visible and
      // inert — every control disabled, `CS-EDIT-DISABLED` standing — which is not what "skippable but never
      // hidden" means. The route issues the ledger-axis token with a `-` read axis, and this consumes it.
      dispatch({ type: 'issued', cvId, precondition: preconditionOf(res.data) });
      setLedgerKey((k) => k + 1);
      return setState({ kind: 'confirming' });
    }
    if (res.status === 409 && body.error === 'no_ledger') {
      if (startExtraction && journeyRan.current !== `${cvId}:${reloadKey}`) {
        journeyRan.current = `${cvId}:${reloadKey}`;
        return await runExtraction(alive);
      }
      return setState({ kind: 'no_stored_read' });
    }
    if (res.status === 404 && body.error === 'cv_not_found') return setState({ kind: 'empty' });
    if (res.status >= 500) return setState({ kind: 'error' });
    // An answer this contract does not recognize, from a backend that just told us we ARE in the cohort. Fail
    // closed to unavailability — never to a legacy mount.
    setState({ kind: 'unavailable' });
  }, [cvId, onPaywall, routeDisabled, runExtraction, startExtraction, reloadKey]);

  useEffect(() => {
    void load();
    return () => {
      // Supersede any in-flight run: its results belong to a CV or a reload generation this view has left behind.
      gen.current += 1;
    };
  }, [load, reloadKey]);

  /**
   * THE LEDGER, READ-ONLY — the evidence lookup's source (§4.2) and the entry labels' source.
   *
   * Fetched separately and NEVER blocking: the review renders with or without it. Without it, "show source"
   * affordances and entry pointers do not appear, which is the honest degradation — an affordance that cannot
   * resolve is not shown at all.
   */
  useEffect(() => {
    let live = true;
    // CLEARED FIRST, SYNCHRONOUSLY, BEFORE THE REQUEST (spec rev 14, closing the review's finding 8).
    //
    // These three maps are keyed by fact id and entry id, and their VALUES are the applicant's own words. They
    // used to persist until the NEXT ledger request succeeded — so a component reused for a second CV kept the
    // first CV's spans and titles on screen for as long as the second fetch took, and FOREVER if that fetch
    // failed or 404'd (the failure paths return without setting anything). Entry and fact ids are not globally
    // unique across CVs, so a matching id rendered the previous CV's quoted text under this CV's band. Resetting
    // here makes the worst case an affordance that does not appear, which is this surface's own rule for evidence
    // it cannot resolve — never someone else's sentence.
    setEvidence(EMPTY_EVIDENCE_INDEX);
    setEntryHeadings(new Map());
    setLedgerEntries(new Map());
    void (async () => {
      const res = await api<LedgerPayload>(`/api/fact-ledger/${cvId}`).catch(() => null);
      if (!live || !res || !res.ok || !res.data) return;
      setEvidence(buildEvidenceIndex(res.data));
      setEntryHeadings(buildEntryHeadings(res.data));
      const entries = (res.data.entries ?? []) as unknown as EntryRecord[];
      setLedgerEntries(new Map(entries.map((e) => [e.entry_id, e])));
    })();
    return () => {
      live = false;
    };
    // `ledgerKey` joins the source list in R5.3: an extraction creates a ledger that did not exist when this
    // effect last ran, and the confirmation screen it routes to has nothing to show until this refetches.
  }, [cvId, reloadKey, ledgerKey]);

  useEffect(() => {
    onScreen.current = state.kind === 'read' ? state.read : null;
  }, [state]);

  /**
   * THE PRECONDITION IS RESET WHEN ITS SOURCE CHANGES — synchronously, before anything can present it.
   *
   * The same rule, and the same reason, as the applicant-text maps above (§1.5, rev 14, finding 8): a token names
   * a ledger, ledger ids are not interchangeable across CVs, and a token that outlived its CV would be presented
   * against a ledger it does not name. The failure mode this forecloses is worse than a stale render — it is a
   * WRITE against another CV's state — so it is cleared here rather than merely replaced when the next read lands.
   */
  useEffect(() => {
    dispatch({ type: 'reset', cvId });
    setPreviousRead(null);
    setFocusSection(null);
    // The escalation notice describes a correction on THIS source. Carrying it across would put a notice about
    // CV A's flagged entry onto CV B's domains — the same class of mistake as carrying the token.
    setAffectedDomains([]);
    // `busy` DESCRIBES AN ACTION ON THIS SOURCE, so it resets with it (round 3, finding 2). It is the corollary
    // of the guards on `refresh`/`upgrade` below: a completion that finds its source gone writes nothing at all,
    // INCLUDING clearing this flag — because clearing it would re-enable controls on a CV whose OWN action may
    // still be in flight. Resetting here is what keeps "writes nothing" from meaning "leaves the next CV stuck".
    setBusy(false);
    // R7.2 — the offer and its notice describe THIS source's ledger position. Both reset with it; the `ranRef` /
    // `failedRef` memories do NOT, because they are keyed by cv and set and are exactly the thing that must
    // survive a re-mount of the same CV.
    setOffer(null);
    setReextractBusy(false);
    setReextractFailed(false);
  }, [cvId, reloadKey]);

  /* ---- §5 — THE CORRECTION LOOP ---------------------------------------------------------------------------- */

  /**
   * The quiet re-fetch behind `CS-CORRECT-CONFLICT`.
   *
   * Deliberately NOT `load()`: that re-asks the bootstrap and drops the surface to `loading`, which would take the
   * conflict notice off the screen and replace the review with a spinner at the exact moment the applicant is
   * being told to re-read it. This refreshes the read and the token in place and leaves the notice standing.
   */
  const refetchRead = useCallback(async () => {
    // THE SOURCE THIS REFETCH BELONGS TO, captured before the request goes out (finding 3).
    const mySource = sourceKey;
    const res = await api<ReadEnvelope & ErrorBody>(`/api/rubric-read/cv/${cvId}`).catch(() => null);
    // A REFETCH FOR A CV WE HAVE LEFT WRITES NOTHING. Not the token, not the read, not the diff baseline: this
    // is a conflict recovery, so its response is a read of CV A, and installing it over CV B would put another
    // applicant's review on screen under this CV's chrome.
    if (sourceRef.current !== mySource) return null;
    if (!res || !res.ok || !res.data?.read || !isValidPublicShape(res.data.read)) return null;
    const fresh = preconditionOf(res.data);
    dispatch({ type: 'issued', cvId, precondition: fresh });
    setOffer(offerOf(res.data));
    setPreviousRead(null);
    setState({ kind: 'read', read: res.data.read });
    // RETURNED, not only dispatched (R7.2): a caller that must present the fresh token on its NEXT request cannot
    // read it back through the reducer — the state it wrote is not visible until the next render — and a retry
    // presenting the token that just conflicted is not a retry.
    return fresh;
  }, [cvId, sourceKey]);

  /** Replace one entry in the ledger map with the server's own echo, so the panel shows what was persisted. */
  const applyEntryEcho = useCallback((echo: unknown) => {
    if (!echo || typeof echo !== 'object') return;
    const id = (echo as { entry_id?: unknown }).entry_id;
    if (typeof id !== 'string') return;
    // MERGED OVER THE RECORD IT REPLACES (R5.7b). The echo is the server's own record of the entry and normally
    // carries every field; the legacy presentation now groups entries by the ledger's `source_section` and names
    // them by their own fields, so a field an echo does not carry must not read as "changed to nothing" and move
    // the entry out of the section the applicant is looking at. A field the echo DOES carry always wins.
    setLedgerEntries((m) => new Map(m).set(id, { ...(m.get(id) ?? {}), ...(echo as EntryRecord) } as EntryRecord));
    // The evidence index and the entry labels are built from the ledger, so a corrected fact's own words have to
    // move with it — otherwise "show source" would keep quoting the sentence the applicant just replaced.
    setEvidence((prev) => mergeEntryEvidence(prev, echo));
    setEntryHeadings((prev) => mergeEntryHeading(prev, echo));
  }, []);

  /**
   * One correction: present the token, apply the answer, never retry.
   *
   * WHAT HAPPENS TO THE SCREEN, per §5 step 4: "the panel updates with server truth". The PATCH response carries
   * the recomputed `RubricReadPublicV1` — already validated by `submitCorrection` through the same structural
   * validator the GET uses — so the whole surface re-renders from it, INCLUDING the layers that have nothing to do
   * with the fact that changed. There is no partial in-place patching of bands, because the bands are not the
   * client's to compute.
   *
   * THE DIFF IS DRAWN AGAINST WHAT WAS ON SCREEN. `previousRead` is set to the read being replaced at the moment
   * it is replaced, which is the only place the copy id for the OLD band still exists (`BandChangeDiff`).
   */
  const correct = useCallback(async (c: Correction) => {
    const token = correction.precondition;
    // Rule 3 + rule 4 of `corrections.ts`, enforced here as well as in the panel's disabled state: a control that
    // is somehow activated without a token must not produce a request.
    if (token === null || correction.busy) return;
    // THE SOURCE THIS CORRECTION BELONGS TO (round 2, finding 3). Captured BEFORE the request, and rechecked
    // before EVERY state write below — not just before the ones that carry a token. A late `conflicted` would
    // raise "this review changed since you opened it" over a CV nothing went wrong with; a late `settled` would
    // restore CV A's token onto CV B and let it be presented against a ledger it does not name.
    const mySource = sourceKey;
    const alive = () => sourceRef.current === mySource;
    dispatch({ type: 'submitting', cvId });
    const result = await submitCorrection(c, token, mintCorrectionKey());
    if (!alive()) return;

    if (result.kind === 'conflict') {
      dispatch({ type: 'conflicted', cvId });
      // §5's recovery, first half: WE re-fetch. The applicant retries — this never resubmits their edit on top of
      // a change they have not seen.
      void refetchRead();
      return;
    }
    if (result.kind === 'requires_recovery') {
      // State X. The correction was refused pending the free `/refresh`, so the surface shows the corrupt state
      // that offers exactly that, rather than reporting a correction problem.
      dispatch({ type: 'settled', cvId, precondition: null });
      setState({ kind: 'corrupt' });
      return;
    }
    if (result.kind === 'rejected') {
      dispatch({ type: 'rejected', cvId, field: result.field, reason: result.reason });
      return;
    }
    if (result.kind === 'failed') {
      dispatch({ type: 'failed', cvId });
      return;
    }

    dispatch({ type: 'settled', cvId, precondition: result.precondition });
    applyEntryEcho(result.entry);
    // §5 — WHICH DOMAINS THE EXCLUSION REACHED. Server-derived over the read this correction replaced, and held
    // here so Layer 3 can carry `CS-FLAG-ESCALATED` beside the ledger card that already does.
    setAffectedDomains(result.affectedDomainIds);
    if (result.kind === 'recomputed') {
      // THE READ BEING REPLACED IS THE DIFF'S `before`, taken from the ref that mirrors what is on screen rather
      // than from a `setState` updater. Setting state from inside another state updater is a side effect in a
      // function React is entitled to call twice (StrictMode does exactly that), and the second call would set
      // `previousRead` to the read the FIRST call had already installed — a diff of a payload against itself.
      setPreviousRead(onScreen.current);
      setState({ kind: 'read', read: result.read });
    } else if (result.kind === 'replayed' && result.read) {
      // A replay appended nothing, so there is nothing to diff — the stored result IS what is already on screen.
      setPreviousRead(null);
      setState({ kind: 'read', read: result.read });
    }
  }, [correction.precondition, correction.busy, refetchRead, applyEntryEcho, cvId, sourceKey]);

  /**
   * §3's proceed control: leave the confirmation screen and run the gated initial compute (the GET).
   *
   * IT IS THE ONLY THING THAT SENDS THE CREDENTIAL, and the credential it sends is THE TOKEN THIS SCREEN WAS
   * ARMED WITH — `correction.precondition`, issued by the `needs_confirmation` 409 (or, on the first pass, by the
   * extraction that created the ledger) and stored opaquely, never assembled from parts. That is what makes the
   * skip an action taken against a state the applicant was SHOWN: the request proves which ledger state was on
   * screen when the button was pressed, and the server refuses it if that state has since moved.
   *
   * NO TOKEN ⇒ SEND NONE, and let the server say so. A ledger-only correction made on this very screen bumps the
   * head revision and re-issues; a missing or stale token simply produces the screen again, armed with the
   * current one. Guessing, incrementing, or falling back to a bare flag would each be the client asserting a
   * state it never observed.
   */
  const proceedFromConfirmation = useCallback(() => {
    void load(correction.precondition);
  }, [load, correction.precondition]);

  /**
   * `/refresh` — FREE and ungated (pure recompute), valid in freshness states S and X only.
   *
   * SOURCE-GUARDED ON EVERY POST-AWAIT BRANCH (round 3, finding 2). `correct` and `refetchRead` captured their
   * source and rechecked it; these two action routes did not, and they are started by the applicant exactly like
   * a correction is — so a `/refresh` for CV A resolving after the parent re-rendered this component for CV B
   * installed A's read, A's token and A's `busy` flag under B's chrome. Every branch below is guarded, not just
   * the ones that write a read: the error branches raise notices about a CV nobody is looking at, and the
   * `onPaywall` callback tells the PARENT to open a checkout for the wrong CV, which is the worst of the three.
   */
  const refresh = useCallback(async () => {
    const mySource = sourceKey;
    const alive = () => sourceRef.current === mySource;
    setBusy(true);
    const res = await api<ReadEnvelope & ErrorBody>(`/api/rubric-read/cv/${cvId}/refresh`, {
      method: 'POST',
    }).catch(() => null);
    if (!alive()) return;
    setBusy(false);
    // A TRANSPORT FAILURE IS A REFUSAL, not a render: the affordance says so and the review on screen stands.
    if (!res) return setRefreshNote('disabled');
    if (res.ok && res.data?.read) {
      // VALIDATED LIKE THE GET (round-2 finding 3). The action routes return the same envelope from the same
      // projection, so they get the same refusal: a body this contract cannot recognize never becomes a render.
      if (!isValidPublicShape(res.data.read)) return setState({ kind: 'unavailable' });
      // AND ITS SIBLING TOKEN IS CONSUMED (round 2, finding 2). The route issues one; round 1 destructured only
      // `read` and dropped it, so a perfectly current read came back EDIT-DISABLED until the applicant reloaded
      // the page — a refresh that silently took editing away is worse than no refresh affordance at all.
      dispatch({ type: 'issued', cvId, precondition: preconditionOf(res.data) });
      setOffer(offerOf(res.data));
      setRefreshNote('none');
      setPreviousRead(null);
      setAffectedDomains([]);
      setState({ kind: 'read', read: res.data.read });
      return;
    }
    const body = (res.data ?? {}) as ErrorBody;
    if (res.status === 409 && body.error === 'corrupt_unrecoverable_no_ledger') {
      // A PAID CV THAT CANNOT BE REGENERATED. Reported as its own state with NO retry affordance, because there
      // is none — and never as `paywall_required`, which would be false entitlement reporting.
      return setState({ kind: 'unrecoverable' });
    }
    if (res.status === 402 && body.reason === 'needs_paid_cv') {
      onPaywall?.(cvId);
      return setState({ kind: 'paywall' });
    }
    setRefreshNote('disabled');
  }, [cvId, onPaywall, sourceKey]);

  /* ---- R7.2 — TAKING THE §6b SIGNALS RE-EXTRACTION OFFER --------------------------------------------------- *
   *
   * THE LIVE GAP THIS CLOSES. Activating a specialty's ratified signal set does nothing for any CV extracted
   * before it existed: those ledgers hold no `specialty_signal` facts of the active set, so `readSpecialtySignal`
   * binds nothing and EVERY §6b row renders `CS-CONV-UNBOUND` — forever, because the offer that names the way out
   * was served and never taken. This takes it.
   *
   * THE RULES IT RUNS UNDER, each one a line below:
   *   * ONCE per CV and set (`ranRef`), and never a second automatic attempt after a failure (`failedRef`).
   *   * NEVER while another action is in flight (`busy`) — the correction loop and this write share one guard
   *     namespace, and a pass racing a correction is a 409 by construction.
   *   * The precondition is presented as `If-Match`, exactly as a correction's is; the route takes NO body (its
   *     expected set is derived server-side from the CV's own specialty, and re-asserting it here would be this
   *     client naming a set it cannot verify).
   *   * A 409/412 is a POSITION problem, not a failure: refetch once for a fresh token and retry once. The route
   *     answers `signals_current` with 409 too — a re-extraction that turned out to be unnecessary — and that
   *     path also just refreshes, because the read may still be showing the pre-facts panel.
   *   * IT NEVER BLOCKS THE READ. Nothing here touches `state` except through the refresh's own validated
   *     replacement; the review, its corrections and every other layer stay live throughout.
   */
  const runReextract = useCallback(async (manual: boolean) => {
    const current = offer;
    if (current === null || busy || reextractBusy) return;
    const key = `${cvId}:${current.expected_set_version}`;
    if (!manual && (ranRef.current.has(key) || failedRef.current.has(key))) return;
    ranRef.current.add(key);
    const mySource = sourceKey;
    const alive = () => sourceRef.current === mySource;
    setReextractBusy(true);
    setReextractFailed(false);

    const fail = () => {
      failedRef.current.add(key);
      setReextractBusy(false);
      setReextractFailed(true);
    };

    // THE POST, with the token this read was served with. `attempt` returns the response or `null` on transport
    // failure; the caller decides what a given status means.
    const attempt = async (token: string | null) => {
      const headers: Record<string, string> = token ? { 'If-Match': token } : {};
      return await api<ErrorBody>(current.endpoint, { method: 'POST', headers }).catch(() => null);
    };

    let res = await attempt(correctionRef.current);
    if (!alive()) return;
    if (res && (res.status === 409 || res.status === 412) && res.data?.error !== 'signals_current') {
      // ONE refetch, ONE retry. The refetch installs the fresh token in the reducer, so it is read back from the
      // ref rather than threaded — the same token every other writer on this surface presents.
      const fresh = await refetchRead();
      if (!alive()) return;
      res = await attempt(fresh ?? correctionRef.current);
      if (!alive()) return;
    }
    const currentAlready = res !== null && res.status === 409 && res.data?.error === 'signals_current';
    if (!res || (!res.ok && !currentAlready)) return fail();

    // THE SECOND HALF, which the offer names: the §6b panel is STORED, so repopulating the ledger changes nothing
    // on screen until the read is recomputed. A refresh that fails is the whole operation failing, because the
    // applicant would otherwise be looking at the same Unbound rows with no notice at all.
    const refreshed = await api<ReadEnvelope & ErrorBody>(current.then_refresh, { method: 'POST' }).catch(() => null);
    if (!alive()) return;
    if (!refreshed || !refreshed.ok || !refreshed.data?.read || !isValidPublicShape(refreshed.data.read)) return fail();
    dispatch({ type: 'issued', cvId, precondition: preconditionOf(refreshed.data) });
    setOffer(offerOf(refreshed.data));
    setPreviousRead(null);
    setAffectedDomains([]);
    setState({ kind: 'read', read: refreshed.data.read });
    setReextractBusy(false);
    setReextractFailed(false);
  }, [offer, busy, reextractBusy, cvId, sourceKey, refetchRead]);

  /**
   * THE AUTO-RUN. It fires only on a rendered read (never over a loading or error screen), only for an offer whose
   * reason is one of the two this surface recognizes, and only once — `runReextract`'s own guards are the
   * authority; this effect is just what asks.
   */
  useEffect(() => {
    if (state.kind !== 'read' || offer === null) return;
    void runReextract(false);
  }, [state.kind, offer, runReextract]);

  /**
   * `/upgrade` — state L only, GATED (the no-ledger case costs an extraction).
   *
   * Guarded identically to `refresh` above, and for the identical reason (round 3, finding 2). This one carries
   * the extra weight: an upgrade's response is a BRAND-NEW read plus a token that arms editing on it, so a late
   * completion did not merely show CV A's review under CV B — it left B's panel armed with A's precondition, from
   * which the next correction would be presented against a ledger it does not name.
   */
  const upgrade = useCallback(async () => {
    if (busy) return;
    const myCv = cvId;
    // SAME CV ⇒ ALWAYS SETTLE (E-5f). A different CV ⇒ write nothing; the source-change effect above has already
    // reset `busy` for the CV that replaced this one.
    const alive = () => cvRef.current === myCv;
    setBusy(true);
    const res = await api<ReadEnvelope & ErrorBody>(`/api/rubric-read/cv/${cvId}/upgrade`, {
      method: 'POST',
    }).catch(() => null);
    if (!alive()) return;
    setBusy(false);
    if (!res) return setState({ kind: 'error' });
    if (res.ok && res.data?.read) {
      if (!isValidPublicShape(res.data.read)) return setState({ kind: 'unavailable' });
      // The sibling token, consumed — same reason as `refresh` above. An upgrade produces a brand-new read whose
      // whole point is that the applicant can now correct it; leaving it read-only until a reload defeats that.
      dispatch({ type: 'issued', cvId, precondition: preconditionOf(res.data) });
      setPreviousRead(null);
      setAffectedDomains([]);
      // THE LEDGER IS REFETCHED: the no-ledger upgrade CREATED it, so the ledger effect's last run found nothing
      // and the read would render with no entry labels, no facts and no evidence until a reload.
      setLedgerKey((k) => k + 1);
      return setState({ kind: 'read', read: res.data.read });
    }
    const body = (res.data ?? {}) as ErrorBody;
    if (res.status === 402 && body.reason === 'needs_paid_cv') {
      onPaywall?.(cvId);
      return setState({ kind: 'paywall' });
    }
    if (res.status === 409 && body.error === 'needs_confirmation') {
      // §3's CONFIRMATION POINT, reached through the upgrade (the route's own batch handler issues it when the
      // ledger head moved under the compute). Routed exactly as the fresh-CV journey routes it: armed with the
      // issued token, ledger refetched, screen shown — never reported as an error.
      dispatch({ type: 'issued', cvId, precondition: preconditionOf(res.data) });
      setLedgerKey((k) => k + 1);
      return setState({ kind: 'confirming' });
    }
    setState({ kind: 'error' });
  }, [busy, cvId, onPaywall]);

  // TWO STATES RENDER NO RUBRIC CHROME AT ALL, and the reason is §3's own rule: "flag-off users see the legacy
  // view unchanged". A flag-off account must not get this surface's sticky header and intended-use statement
  // wrapped around its legacy read — that would be the new surface half-shipping to a cohort it is not enabled
  // for. The same applies while the decision is still unknown: chrome first and a 403 second is a flash of a
  // surface the account may not be in.
  if (state.kind === 'loading') {
    // THE WAIT IS ANNOUNCED **AND SAID** (round 2, finding 8). Round 1 rendered an EMPTY `role="status"` region,
    // on the reasoning that Appendix A registered no loading string — which the review rejected: an empty live
    // region announces nothing at all, so a screen-reader user was told the page had stopped rather than that it
    // was working. `CS-READ-LOADING` is registered (A.5-P) and rendered here.
    return (
      <div role="status" aria-busy="true" data-testid="rubric-loading">
        <Copy id="CS-READ-LOADING" />
      </div>
    );
  }
  if (state.kind === 'extracting') {
    // THE SAME ANNOUNCED WAIT, and this is the one that matters most: an extraction is the longest wait on the
    // surface, so silence here is the longest silence.
    return (
      <div role="status" aria-busy="true" data-testid="rubric-extracting">
        <Copy id="CS-EXTRACTION-LOADING" />
      </div>
    );
  }
  if (state.kind === 'extract_unavailable') {
    return (
      <p style={NOTICE_STYLE} role="alert" data-testid="rubric-extract-unavailable" data-refusal={state.copyId}>
        <Copy id={state.copyId} />
      </p>
    );
  }
  if (state.kind === 'unavailable') {
    // NO LEGACY MOUNT HERE, DELIBERATELY. `CS-RUBRIC-DISABLED` would assert a server decision nobody reported,
    // and rendering `legacyView` would mount the surface whose own read path recomputes — on exactly the backend
    // we just failed to identify. The registered unavailability string is the whole answer.
    return (
      <p style={NOTICE_STYLE} role="alert" data-testid="rubric-unavailable">
        <Copy id="CS-ERR-READ-UNAVAILABLE" />
      </p>
    );
  }
  if (state.kind === 'disabled') {
    return (
      <div data-testid="rubric-disabled">
        <p style={NOTICE_STYLE}>
          <Copy id={state.hasLegacy ? 'CS-RUBRIC-DISABLED' : 'CS-RUBRIC-DISABLED-NO-LEGACY'} />
        </p>
        {/* The legacy view is reachable independently of the flag (its endpoint is flag-exempt), which is what
            makes this a real fallback rather than a claimed one. */}
        {state.hasLegacy ? legacyView : null}
      </div>
    );
  }
  if (state.kind === 'no_stored_read') {
    // R5-OQ-G's INTERIM ROUTE, corrected in round 2. It renders no rubric chrome for the same reason `disabled`
    // does not: this CV has no rubric read, so wrapping anything in this surface's sticky header and intended-use
    // statement would half-ship a review that does not exist.
    //
    // AND IT DOES NOT MOUNT `legacyView`, WHICH IS THE CORRECTION. This state is reached only by a COHORT account
    // (step 1 said so) — and for a cohort account the legacy surface cannot load: `/quality` answers it with the
    // R5 pointer before any recompute (§0a item 6(b)), which the legacy surface reads as a failed load. So the
    // old branch showed a cohort applicant an error where it meant to show them a fallback.
    //
    // THERE IS ALSO NOTHING PREVIOUS-FORMAT TO SHOW HERE, and that is not an assumption: `GET /cv/:id` checks L
    // BEFORE N, so a CV with a stored legacy read answers `legacy_only` and never reaches this state. `no_ledger`
    // means no rubric read AND no legacy read. The honest answer is the ratified empty string — "No review yet.
    // Import your CV to generate one." — and, when the parent gave us one, a way back to the flow that makes one.
    // The creation journey itself (import → extraction → confirmation → read) is R5.3's, and lives outside this
    // component; this state's whole job until then is to say what is true and not strand anybody.
    return (
      <div data-testid="rubric-no-stored-read">
        {onImportCv ? (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onImportCv}
            data-testid="rubric-empty-import"
          >
            {/* The control's label IS the registered sentence for this state, which already names the action. No
                new CTA string is invented here — Appendix A registers none, and T-COPY-1 admits no unregistered
                rendered text. */}
            <Copy id="CS-EMPTY-NO-READ" />
          </button>
        ) : (
          <p style={NOTICE_STYLE} data-testid="rubric-empty">
            <Copy id="CS-EMPTY-NO-READ" />
          </p>
        )}
      </div>
    );
  }

  if (state.kind === 'read') {
    return (
      <LegacyRead
        cvId={cvId}
        beta={beta}
        specialtyName={specialtyName}
        read={state.read}
        busy={busy}
        refreshNote={refreshNote}
        evidence={evidence}
        entryHeadings={entryHeadings}
        ledgerEntries={ledgerEntries}
        onRefresh={refresh}
        correction={correction}
        previousRead={previousRead}
        affectedDomains={affectedDomains}
        onCorrect={correct}
        onPaywall={onPaywall}
        onHome={onHome}
        onCraft={onCraft}
        focusSection={focusSection}
        onFocusSection={setFocusSection}
        reextract={offer === null && !reextractBusy && !reextractFailed ? null : {
          busy: reextractBusy,
          failed: reextractFailed,
          onRetry: () => { void runReextract(true); },
        }}
      />
    );
  }

  return (
    <main style={PAGE_STYLE} data-testid="rubric-read-view">
      <IntendedUseHeader beta={beta} />
      <IntendedUse />
      <Body
        cvId={cvId}
        state={state}
        busy={busy}
        refreshNote={refreshNote}
        ledgerEntries={ledgerEntries}
        onRefresh={refresh}
        onUpgrade={upgrade}
        correction={correction}
        onCorrect={correct}
        onProceed={proceedFromConfirmation}
      />
    </main>
  );
}

function Body({
  cvId,
  state,
  busy,
  refreshNote,
  ledgerEntries,
  onRefresh,
  onUpgrade,
  correction,
  onCorrect,
  onProceed,
}: {
  cvId: string;
  state: RubricViewState;
  busy: boolean;
  refreshNote: RefreshNote;
  ledgerEntries: LedgerEntryIndex;
  onRefresh: () => void;
  onUpgrade: () => void;
  correction: CorrectionState;
  onCorrect: (c: Correction) => void;
  onProceed: () => void;
}) {
  switch (state.kind) {
    // `loading`, `disabled`, `no_stored_read`, `unavailable`, `extracting`, `extract_unavailable` and — since
    // R5.7b — `read` are all handled before any chrome renders (see `RubricReadView`), so they cannot reach here;
    // the cases stay for exhaustiveness rather than being asserted away.
    case 'loading':
    case 'disabled':
    case 'no_stored_read':
    case 'unavailable':
    case 'extracting':
    case 'extract_unavailable':
    case 'read':
      return null;

    case 'confirming':
      // §3's CONFIRMATION SCREEN — inside the surface chrome, deliberately. §4 pins `CS-INTENDED-USE-SHORT` in the
      // sticky header "at all times", and the moment the applicant is checking the facts a review will be computed
      // from is precisely the moment the intended-use statement is load-bearing. It renders with NO layer stack,
      // because there is no read yet: this is Layer 1 alone, which is exactly what §3 asks for.
      return (
        <FactLedgerPanel
          entries={ledgerEntriesArray(ledgerEntries)}
          mode="confirm"
          state={correction}
          onCorrect={onCorrect}
          onProceed={onProceed}
        />
      );

    case 'paywall':
      // The paywall modal is the parent's, reached through `onPaywall` — the same wiring the coverage read uses.
      return null;

    case 'error':
      return (
        <p style={NOTICE_STYLE} role="alert" data-testid="rubric-error">
          <Copy id="CS-ERR-READ-UNAVAILABLE" />
        </p>
      );

    case 'empty':
      return (
        <p style={NOTICE_STYLE} data-testid="rubric-empty">
          <Copy id="CS-EMPTY-NO-READ" />
        </p>
      );

    case 'unrecoverable':
      // NO RETRY AFFORDANCE — deliberately. `CS-READ-CORRUPT-UNRECOVERABLE` exists precisely because the other
      // corrupt string promises an action that this state cannot deliver.
      return (
        <p style={NOTICE_STYLE} role="alert" data-testid="rubric-unrecoverable">
          <Copy id="CS-READ-CORRUPT-UNRECOVERABLE" />
        </p>
      );

    case 'corrupt':
      return (
        <div data-testid="rubric-corrupt">
          <p style={NOTICE_STYLE} role="alert">
            <Copy id="CS-READ-CORRUPT" />
          </p>
          <RefreshControl busy={busy} refreshNote={refreshNote} onRefresh={onRefresh} />
        </div>
      );

    case 'legacy_only':
      return (
        <div data-testid="rubric-legacy-only">
          {/* THE WAIT IS VISIBLE AND ANNOUNCED (E-5f). An upgrade on a no-ledger CV IS an extraction — the longest
              wait on the surface — and a disabled button was its only sign. The same registered status region the
              `extracting` state renders, for the same reason. */}
          {busy && (
            <div role="status" aria-busy="true" data-testid="rubric-extracting">
              <Copy id="CS-EXTRACTION-LOADING" />
            </div>
          )}
          <button type="button" className="btn btn-secondary" onClick={onUpgrade} disabled={busy} aria-busy={busy} data-testid="rubric-upgrade">
            <Copy id="CS-UPGRADE-CTA" />
          </button>
          {/* THE STORED LEGACY REVIEW ITSELF (round-2 finding 1), through the dedicated `/legacy` endpoint —
              never `legacyView`, which is `QualityReadView` and cannot load for a cohort account (§0a item 6(b)
              answers it with the R5 pointer before any recompute). `LegacyStoredView` carries `CS-LEGACY-BANNER`
              at its own top, so the banner is not repeated here. The upgrade CTA stays above it: the user is not
              stranded into buying a new review to see the one they have. */}
          <LegacyStoredView cvId={cvId} />
        </div>
      );
  }
}

/**
 * The ledger map as an array, in insertion (ledger) order.
 *
 * The map exists because `Recommendations` looks entries up by id; Layer 1 walks them instead. One source, two
 * shapes — never two fetches, and never a second copy of the ledger held in a different piece of state that could
 * disagree with this one about what the applicant last corrected.
 */
function ledgerEntriesArray(m: LedgerEntryIndex): LedgerEntryLike[] {
  return [...m.values()] as unknown as LedgerEntryLike[];
}

/** The refresh affordance, plus the honest note when a refresh could not run. Both strings are registered. */
function RefreshControl({
  busy,
  refreshNote,
  onRefresh,
}: {
  busy: boolean;
  refreshNote: RefreshNote;
  onRefresh: () => void;
}) {
  return (
    <>
      <button type="button" className="btn btn-secondary" onClick={onRefresh} disabled={busy} data-testid="rubric-refresh">
        <Copy id="CS-REFRESH-VERSION-CTA" />
      </button>
      {refreshNote === 'disabled' && (
        <p style={{ ...BODY_STYLE, marginTop: 'var(--space-2)' }} role="status" data-testid="rubric-refresh-disabled">
          <Copy id="CS-REFRESH-DISABLED" />
        </p>
      )}
    </>
  );
}

/* ============================================================================================================== *
 * R5.7b — THE READ, THROUGH THE LEGACY PRESENTATION (owner directive 2026-08-22, final)
 *
 * "we iterated a lot to arrive at the current UI; copy it AS IS — the change is the content from the engine, not
 * the display." The R5.7 look-alike entry cards are gone from this state. What renders is `QualityReadView`'s own
 * L1 overview + L2 section review (`QualityReadBody`: same components, same JSX, same styles, same placements),
 * fed by `toLegacyQualityRead` — every system string a registered id, every applicant string the ledger's own —
 * with exactly these kept / added:
 *   * the intended-use banner + the beta label (codebook §1.3 / §8.3) as the page preamble;
 *   * the registered notices the codebook mandates and the legacy page has no slot for (input-surface notice,
 *     `CS-STALE-VERSION`, the correction diff, the correction-loop notices, the specialty panel's generic-mode
 *     notice / mandatory hedges), in the legacy overview's notice position;
 *   * ONE text link per legacy entry card (`CS-ENTRY-FACTS-LINK`, in the card's existing action area) to that
 *     entry's card on the FACTS SCREEN — the rubric ledger panel ("Facts read from your CV"), rendered below the
 *     legacy body, where each entry's facets, descriptors, confidence line and known-fact / phrasing
 *     recommendations are reported (R5.7b fix: nothing of the kind inside a legacy card).
 * The legacy "↻ Re-run review" control is the free `/refresh`; "Reviewed {when}" is `assessed_at`.
 * ============================================================================================================== */

const NOOP = () => {};

export function LegacyRead({
  cvId,
  beta,
  specialtyName,
  read,
  busy,
  refreshNote,
  evidence,
  entryHeadings,
  ledgerEntries,
  onRefresh,
  correction,
  previousRead,
  affectedDomains,
  onCorrect,
  onPaywall,
  onHome,
  onCraft,
  focusSection,
  onFocusSection,
  reextract = null,
}: {
  cvId: string;
  beta: boolean;
  specialtyName: string;
  read: RubricReadPublicV1;
  busy: boolean;
  refreshNote: RefreshNote;
  evidence: EvidenceIndex;
  entryHeadings: ReadonlyMap<string, EntryHeading>;
  ledgerEntries: LedgerEntryIndex;
  onRefresh: () => void;
  correction: CorrectionState;
  previousRead: RubricReadPublicV1 | null;
  affectedDomains: readonly string[];
  onCorrect: (c: Correction) => void;
  onPaywall?: (cvId: string) => void;
  onHome?: () => void;
  onCraft?: () => void;
  focusSection: string | null;
  onFocusSection: (section: string | null) => void;
  /** R7.2 — the §6b re-extraction wait / retry, passed straight through to the specialty slot. */
  reextract?: ConventionReextractState | null;
}) {
  // The ledger as the adapter reads it — FROM THE SAME MAP the correction loop updates, so a corrected title or
  // text moves in the legacy cards too (never a second copy of the ledger that could disagree with the panel).
  const ledgerPayload = useMemo<LedgerPayload>(
    () => ({ entries: [...ledgerEntries.values()] as unknown as LedgerPayload['entries'] }),
    [ledgerEntries],
  );
  const adapted = useMemo(() => toLegacyQualityRead(read, ledgerPayload), [read, ledgerPayload]);
  const editable = canSubmit(correction);
  const byId = useMemo(() => new Map(read.entries.map((e) => [e.entry_id, e] as const)), [read.entries]);

  // THE ONE LINK a legacy entry card carries: to the entry's card on the facts screen below. Rendered only for an
  // entry the ledger has (the facts screen lists the ledger's entries); following it opens the entry's facts
  // disclosure and moves focus to its summary (the same in-page behaviour as the confidence line's link).
  const entryFactsLink: EntryFactsLink = {
    label: COPY['CS-ENTRY-FACTS-LINK'],
    /**
     * 2026-08-23 (owner, on a real OB/GYN CV: "this is so disorganized… the left-right entry by entry review is
     * what it is meant to be. other details could still slot in, but that seems like a raw database").
     *
     * The entry's facts, its engine report and its correction controls now render INLINE, in the entry's own
     * card, behind the disclosure `EntryFactsDisclosure` already provides. They used to live in a whole-CV
     * `FactLedgerPanel` mounted BELOW the read, which meant every entry appeared twice on the page — once as a
     * left/right review row and again as a fact card — and the applicant had to travel between them. One
     * reading layer; the rest one click away, on the thing it is about.
     */
    detailsFor: (entryId) => {
      const rec = ledgerEntries.get(entryId);
      if (rec === undefined) return null;
      return (
        <>
          {/* BUG FIX 2026-08-23 — the entry's STATE NOTICES came along with the rest of the card.
              `EntryStateNotices` (extraction-uncertain, and §5's `CS-FLAG-ESCALATED` for an entry the applicant
              flagged wrong) used to render on the whole-CV panel's `LedgerEntryCard`. When that panel was
              deleted, these two notices stopped rendering ANYWHERE — §5 requires the escalation on the entry as
              well as on its affected domains, and the applicant otherwise gets no confirmation that the entry
              they flagged left the computation. They are notices ABOUT the entry, so they render above its
              disclosure, exactly where the card had them. */}
          <EntryStateNotices entry={rec as unknown as LedgerEntryLike} />
          <EntryFactsDisclosure
            entry={rec as unknown as LedgerEntryLike}
            heading={entryHeadings.get(entryId) ?? null}
            defaultOpen={false}
            editable={editable}
            rejected={correction.rejected}
            onCorrect={onCorrect}
            // INSIDE the disclosure, not beside it (Sol review, 2026-08-23). Rendered as a sibling, the facets,
            // descriptors, confidence line and recommendations stayed on screen under a CLOSED control — the
            // "raw database" was still there, just below a summary claiming it was tucked away.
            report={entryReport(entryId, 1)}
          />
        </>
      );
    },
    hrefFor: (entryId) => (ledgerEntries.has(entryId) ? `#${ledgerEntryDomId(entryId)}` : null),
    onFollow: (entryId, e) => {
      const details = document.getElementById(ledgerEntryDomId(entryId));
      if (!(details instanceof HTMLDetailsElement)) return;
      e.preventDefault();
      details.open = true;
      const summary = details.querySelector('summary');
      if (summary instanceof HTMLElement) summary.focus();
      if (typeof details.scrollIntoView === 'function') details.scrollIntoView({ block: 'nearest' });
    },
  };

  // THE FACTS SCREEN'S PER-ENTRY REPORT — what the engine read from the entry, in §4's fixed sub-order: facets
  // (the §3.0 framing ONCE, above the first reported entry) → descriptors → confidence line → the known-fact /
  // suggested-phrasing recommendations (the questions are the legacy rows). A ledger-only entry (not assessed)
  // reports nothing here; its facts still render.
  const entryReport = (entryId: string, index: number): ReactNode => {
    const entry = byId.get(entryId) ?? null;
    if (entry === null) return null;
    return (
      <div data-testid="rubric-ledger-entry-report" data-entry-id={entryId}>
        {/* THE §3.0 FACET FRAMING IS NOT RENDERED (owner, 2026-08-23: "some things seem like you talking to
            yourself, not the user… it sounds like you are justifying your approach to a user"). It read "These
            levels describe what your CV states, in increasing detail about your relationship to the work. No
            level is better than another…" — a paragraph explaining our own ladder, above content the applicant
            came for. `CS-FACET-FRAMING` stays REGISTERED; only its rendering stopped. */}
        <EntryFacets entry={entry} evidence={evidence} />
        <EntryDescriptors entry={entry} evidence={evidence} />
        <EntryConfidenceLine entry={entry} heading={entryHeadings.get(entryId) ?? null} />
        <EntryRecommendations
          entryId={entryId}
          recommendations={nonQuestionRecommendations(read.recommendations)}
          evidence={evidence}
          ledgerEntries={ledgerEntries}
        />
      </div>
    );
  };

  const notices = (
    <>
      {/* §4 — input-surface notice at top when applicable (the `other_resume` one the engine sets). */}
      {read.input_surface_notice_copy_string_id && (
        <p style={NOTICE_STYLE} data-testid="rubric-input-surface-notice">
          <Copy id={read.input_surface_notice_copy_string_id} />
        </p>
      )}
      {/* THE FRESHNESS ENVELOPE (§1.2c state S). One banner regardless of how many components moved, with its
          registered refresh CTA; the legacy "↻ Re-run review" control at the top of the page runs the same
          free `/refresh`. */}
      {read.freshness === 'stale' && (
        <div style={SECTION_STYLE} data-testid="rubric-stale">
          <p style={NOTICE_STYLE} data-stale-reason={read.stale_reason.join(',')}>
            <Copy id="CS-STALE-VERSION" />
          </p>
          <RefreshControl busy={busy} refreshNote={refreshNote} onRefresh={onRefresh} />
        </div>
      )}
      {read.freshness !== 'stale' && refreshNote === 'disabled' && (
        <p style={{ ...BODY_STYLE, marginTop: 'var(--space-2)' }} role="status" data-testid="rubric-refresh-disabled">
          <Copy id="CS-REFRESH-DISABLED" />
        </p>
      )}
      {/* §5 step 5 — the correction diff, above everything it describes. */}
      <BandChangeDiff previous={previousRead} next={read} />
      {/* §5 / §8.4 — THE FLAG ESCALATION, SECOND LOCATION (round 2, finding 6): every domain the server said lost
          a unit to the correction just made. The legacy domain card has no notice slot, so each renders here,
          naming its domain by the domain's own registered name beside the same registered sentence the ledger
          card carries — not an alert: the band on the card below already moved, and this explains why. */}
      {read.domains.filter((d) => affectedDomains.includes(d.domain)).map((d) => (
        <p key={d.domain} style={NOTICE_STYLE} role="status" data-testid="rubric-domain-flag-escalated" data-domain={d.domain}>
          <Copy id={d.domain_copy_string_id} />
          {' '}
          <Copy id="CS-FLAG-ESCALATED" />
        </p>
      ))}
      {/* §5 — the correction loop's section-level notices. */}
      {correction.conflict && (
        <p style={NOTICE_STYLE} role="alert" data-testid="rubric-correct-conflict">
          <Copy id="CS-CORRECT-CONFLICT" />
        </p>
      )}
      {!editable && (
        <p style={NOTICE_STYLE} role="status" data-testid="rubric-edit-disabled">
          <Copy id="CS-EDIT-DISABLED" />
        </p>
      )}
      {correction.failed && (
        <p style={NOTICE_STYLE} role="alert" data-testid="rubric-correction-failed">
          <Copy id="CS-ERR-READ-UNAVAILABLE" />
        </p>
      )}
    </>
  );

  /* ============================================================================================================ *
   * §4.5 — THE SPECIALTY PANEL, IN THE OVERVIEW'S PANEL POSITION AND *ONLY* THERE (owner, 2026-08-23).
   *
   * It used to sit inside `notices`, and `QualityReadBody` renders `notices` in BOTH its L1 branch and its L2
   * branch — so the entire convention panel (its claim, every ratified row, both empty sub-modules and the
   * footer) reprinted above every single section review. That is one half of the owner's "all entries still show
   * in one section view / the presentation is near haphazard": the section the applicant asked to read was pushed
   * below a full-page panel they had already read on the overview.
   *
   * The panel is a WHOLE-CV statement, so it belongs to the whole-CV screen. It is now passed only when
   * `focusSection === null`. The state notices above (input surface, freshness, correction) stay on both levels:
   * each is about the read the applicant is looking at right now, and suppressing them at L2 would hide a stale
   * or failed state from the only screen they are acting on.
   * ============================================================================================================ */
  const specialtyPanel = (
    <SpecialtyOverlayPanel
      overlay={read.overlay}
      mode={read.mode}
      overlay_status={read.overlay_status}
      evidence={evidence}
      // R7.1 §6b — the convention panel rides in the SAME slot (spec §5). `null` on every read computed before
      // R7.1 or with the layer off, which renders exactly what this slot rendered yesterday.
      convention={read.convention ?? null}
      // R7.2 — the wait and the retry ride in the same slot as the panel they are about.
      reextract={reextract}
    />
  );

  /* ============================================================================================================ *
   * L2 SCOPING — THE FACTS SCREEN FOLLOWS THE SECTION UNDER REVIEW (owner, 2026-08-23: "all entries still show in
   * one section view").
   *
   * `FactLedgerPanel` renders the WHOLE CV's ledger, and it was mounted as an unconditional sibling below
   * `QualityReadBody`. At L1 that is correct — the facts screen is the whole-CV appendix the overview's entry
   * links point into. At L2 it meant every entry of every other section (43 of them on the owner's own CV) piled
   * up underneath the one section they had chosen to read, which is exactly the complaint.
   *
   * The scope is derived from `adapted.sections` — the SAME grouping the L1 cards and the L2 section review are
   * built from — so the panel can never disagree with the screen above it about which entries belong to this
   * section. `entryFactsLink` keeps working because the entries it targets are precisely the ones still mounted.
   * ============================================================================================================ */
  const focusedEntryIds = useMemo<ReadonlySet<string> | null>(() => {
    if (focusSection === null) return null;
    const s = adapted.sections.find((x) => x.section === focusSection);
    if (s === undefined) return new Set<string>();
    return new Set<string>([
      ...(s.items ?? []).map((it) => it.entry_id),
      ...(s.strongEntries ?? []).map((e) => e.entry_id),
    ]);
  }, [adapted.sections, focusSection]);

  const ledgerEntriesShown = useMemo(
    () => (focusedEntryIds === null
      ? (ledgerPayload.entries as unknown as LedgerEntryLike[])
      : (ledgerPayload.entries as unknown as LedgerEntryLike[]).filter((e) => focusedEntryIds.has(String(e.entry_id)))),
    [ledgerPayload.entries, focusedEntryIds],
  );
  const ledgerOrderShown = useMemo(
    () => (focusedEntryIds === null
      ? read.entries.map((e) => e.entry_id)
      : read.entries.map((e) => e.entry_id).filter((id) => focusedEntryIds.has(id))),
    [read.entries, focusedEntryIds],
  );

  return (
    <div data-testid="rubric-read-view">
      <QualityReadBody
        cvId={cvId}
        specialtyName={specialtyName}
        read={adapted}
        onCraft={onCraft ?? NOOP}
        onHome={onHome ?? NOOP}
        onPaywall={onPaywall}
        rerunning={busy}
        onRerun={onRefresh}
        assessedAt={read.assessed_at}
        stale={false}
        changedEntries={null}
        justUpdated={false}
        onInteract={NOOP}
        liveMsg=""
        failed={false}
        failedRetrySafe={false}
        parseQuality={null}
        proceedAnyway={false}
        onProceedAnyway={NOOP}
        focusSection={focusSection}
        onFocusSection={onFocusSection}
        entryFactsLink={entryFactsLink}
        // The engine's rows are clarification prompts, not a drafting box: the legacy anti-fabrication banner is off.
        antiFabrication={false}
        // ALL ENTRIES EXPANDED, so every CV line is a visible row and the highlight can walk them (owner: "make
        // the presentation consistent" — a section must not mix expanded blocks with collapsed "N points" rows).
        collapseAfter={Number.POSITIVE_INFINITY}
        // The legacy "Re-run to add the {Specialty} relevance read" offer promises the LEGACY specialty panel, which
        // this route cannot produce (the specialty panel here is the rubric overlay — generic notice or the rows).
        // Never offered on the rubric path.
        relevanceOffer={false}
        // 2026-08-23 (owner: "some things seem like you talking to yourself, not the user… it sounds like you are
        // justifying your approach to a user"). The intended-use statement used to open the page, above the
        // applicant's own review, alongside a beta chip and a second copy of itself — four restatements of the
        // same disclaimer before a single word about their CV. It is now ONE unobtrusive footer, below the work,
        // rendered exactly once. Nothing about what this product may never claim has changed: the banned-output
        // contract is about the claims, not about how often the disclaimer is repeated.
        preamble={undefined}
        // The specialty panel is OVERVIEW-ONLY (see above); the state notices ride on both levels.
        notices={focusSection === null ? <>{notices}{specialtyPanel}</> : notices}
        // The legacy page's three METHOD statements describe the legacy engine (impact framing, evidence framing,
        // the accuracy checks). None is true of this read, and none is registered copy, so each slot renders
        // nothing: the intended-use statement above the page is the method statement this surface makes.
        intro={null}
        scopeNote={null}
        // L3 — THE ON-DEMAND DETAIL LAYER, which is exactly where the per-area detail belongs now (owner: "raw
        // data is not helpful as the synthesis is"; "display what is relevant to the user and not how we derived
        // it"). `OverviewDetail` holds the eleven domain / presentation band sentences that used to be serialised
        // into the opening paragraph, behind one closed disclosure. Beneath it, the intended-use statement, ONCE,
        // as the page's footer.
        methodology={(
          <>
            <OverviewDetail read={read} ledger={ledgerPayload} />
            <IntendedUseHeader beta={beta} />
            <IntendedUse />
          </>
        )}
      />
      {/* THE WHOLE-CV FACTS PANEL IS GONE FROM HERE. It was the third stacked rendering of the same entries and
          the owner named it: "that seems like a raw database". Each entry's facts, engine report and correction
          controls now render on the ENTRY, through `entryFactsLink.detailsFor` above. Nothing was removed from
          the product — the same components render, in the place the applicant is already looking. */}
    </div>
  );
}
